import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { searchKks } from "../api";
import { useOccurrenceColumnWidths } from "../hooks/useOccurrenceColumnWidths";
import { OccurrenceHeaderRow, OccurrenceRow } from "./KksOccurrenceRow";

interface Props {
  query: string;
  onQueryChange: (value: string) => void;
  onNavigate: (
    plantId: string,
    systemId: string,
    pidId: string,
    kksId: string,
  ) => void;
}

export default function SearchList({
  query,
  onQueryChange,
  onNavigate,
}: Props) {
  const [debouncedQuery, setDebouncedQuery] = useState(() => query.trim());
  const { colWidths, resizeColumn, commitColumnWidths } =
    useOccurrenceColumnWidths("search");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 400);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isFetching } = useQuery({
    queryKey: ["kks-search", debouncedQuery],
    queryFn: () => searchKks(debouncedQuery),
    enabled: debouncedQuery.length > 0,
  });

  const occurrences = debouncedQuery ? (data?.occurrences ?? []) : [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.2,
          borderBottom: "1px solid #DEE3EA",
          flexShrink: 0,
        }}
      >
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder="Type KKS text to search across all P&IDs…"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: isFetching ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {!debouncedQuery && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 3, textAlign: "center" }}
          >
            Start typing to search KKS across all P&IDs.
          </Typography>
        )}

        {debouncedQuery && !isFetching && occurrences.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 3, textAlign: "center" }}
          >
            No matching KKS found.
          </Typography>
        )}

        {occurrences.length > 0 && (
          <>
            <OccurrenceHeaderRow
              variant="search"
              colWidths={colWidths}
              onResize={resizeColumn}
              onResizeEnd={commitColumnWidths}
            />
            {occurrences.map((occ, i) => (
              <OccurrenceRow
                key={occ.kks_id || i}
                occurrence={occ}
                colWidths={colWidths}
                variant="search"
                onClick={() =>
                  onNavigate(
                    occ.plant_id,
                    occ.system_id,
                    occ.pid_id,
                    occ.kks_id,
                  )
                }
              />
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}
